import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  FileText, Download, DollarSign, Ticket, Users, Calendar,
  FileSpreadsheet, Banknote
} from "lucide-react";

export default function AdminReports() {
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState({});

  const downloadReport = async (reportType, format = "csv") => {
    setLoading({ ...loading, [reportType]: true });
    
    try {
      let url = `${API}/admin/reports/${reportType}?format=${format}`;
      if (dateRange.start) url += `&start_date=${dateRange.start}`;
      if (dateRange.end) url += `&end_date=${dateRange.end}`;
      
      const response = await axios.get(url, { 
        withCredentials: true,
        responseType: format !== 'json' ? 'blob' : 'json'
      });
      
      if (format !== 'json') {
        // Download file
        const blob = new Blob([response.data], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${reportType}_report_${new Date().toISOString().slice(0,10)}.${format === 'quickbooks' ? 'iif' : format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success("Report downloaded successfully!");
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    } finally {
      setLoading({ ...loading, [reportType]: false });
    }
  };

  const downloadQuickBooks = async () => {
    setLoading({ ...loading, quickbooks: true });
    
    try {
      let url = `${API}/admin/reports/quickbooks`;
      if (dateRange.start) url += `?start_date=${dateRange.start}`;
      if (dateRange.end) url += `${dateRange.start ? '&' : '?'}end_date=${dateRange.end}`;
      
      window.open(url, '_blank');
      toast.success("QuickBooks export started!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate QuickBooks export");
    } finally {
      setLoading({ ...loading, quickbooks: false });
    }
  };

  const downloadAttendees = async () => {
    setLoading({ ...loading, attendees: true });
    
    try {
      window.open(`${API}/admin/export/attendees?format=csv`, '_blank');
      toast.success("Attendees export started!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to export attendees");
    } finally {
      setLoading({ ...loading, attendees: false });
    }
  };

  return (
    <AdminLayout currentPage="Reports">
      <div className="p-8" data-testid="admin-reports">
        <div className="mb-8">
          <h1 className="text-heading-2 text-deep-blue">Reports & Exports</h1>
          <p className="text-slate-600">Generate reports for accounting and analysis</p>
        </div>

        {/* Date Range Filter */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-deep-blue flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Date Range Filter
            </CardTitle>
            <CardDescription>Select a date range to filter all reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className="w-44"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className="w-44"
                />
              </div>
              <Button variant="outline" onClick={() => setDateRange({ start: "", end: "" })}>
                Clear Dates
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Donations Report */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-deep-blue">Donations Report</CardTitle>
              <CardDescription>
                Export all donations with donor info, amounts, and payment details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  className="w-full btn-primary" 
                  onClick={() => downloadReport("donations", "csv")}
                  disabled={loading.donations}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading.donations ? "Generating..." : "Download CSV"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tickets Report */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Ticket className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-deep-blue">Tickets Report</CardTitle>
              <CardDescription>
                Export ticket sales with event, attendee, and revenue data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  className="w-full btn-primary" 
                  onClick={() => downloadReport("tickets", "csv")}
                  disabled={loading.tickets}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading.tickets ? "Generating..." : "Download CSV"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Attendees Export */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle className="text-deep-blue">Attendees Export</CardTitle>
              <CardDescription>
                Full attendee list with contact info, dietary needs, and notes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  className="w-full btn-primary" 
                  onClick={downloadAttendees}
                  disabled={loading.attendees}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading.attendees ? "Generating..." : "Download CSV"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* QuickBooks Export */}
          <Card className="hover:shadow-lg transition-shadow border-2 border-green-200">
            <CardHeader>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-3">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-deep-blue">QuickBooks Export</CardTitle>
              <CardDescription>
                Generate IIF file compatible with QuickBooks for easy import
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white" 
                  onClick={downloadQuickBooks}
                  disabled={loading.quickbooks}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {loading.quickbooks ? "Generating..." : "Download IIF File"}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Includes donations and ticket sales
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-deep-blue">Financial Summary</CardTitle>
              <CardDescription>
                Overview report with totals, averages, and breakdowns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  className="w-full btn-primary" 
                  onClick={() => {
                    toast.info("Opening summary in new tab...");
                    let url = `${API}/admin/reports/donations?format=json`;
                    if (dateRange.start) url += `&start_date=${dateRange.start}`;
                    if (dateRange.end) url += `&end_date=${dateRange.end}`;
                    window.open(url, '_blank');
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Summary
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-deep-blue">Import Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-deep-blue mb-2">QuickBooks Desktop</h4>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Download the IIF file</li>
                  <li>Open QuickBooks Desktop</li>
                  <li>Go to File → Utilities → Import → IIF Files</li>
                  <li>Select the downloaded file</li>
                  <li>Review imported transactions</li>
                </ol>
              </div>
              <div>
                <h4 className="font-semibold text-deep-blue mb-2">QuickBooks Online</h4>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Download the CSV report</li>
                  <li>Go to Settings → Import Data</li>
                  <li>Choose "Bank Data" or "Invoices"</li>
                  <li>Upload the CSV file</li>
                  <li>Map columns and complete import</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
